import type { ListingMenuKind } from '@pulsify/shared/dto'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { scanMenu } from '../api/scan-menu'
import { prepareUpload } from '../lib/prepare-upload'
import { runMenuScan } from '../lib/run-menu-scan'

/** Runs one reading at a time with a live dish count; leaving the page cancels it. */
export function useMenuScan(businessId: string) {
  const queryClient = useQueryClient()
  const running = useRef<AbortController | null>(null)
  const [dishes, setDishes] = useState<number | null>(null)

  useEffect(() => () => running.current?.abort(), [])

  async function run(listingId: string, kind: ListingMenuKind, files: readonly File[]) {
    running.current?.abort()
    const controller = new AbortController()
    running.current = controller
    setDishes(null)
    try {
      return await runMenuScan(files, {
        prepare: (file) => prepareUpload(file),
        send: (prepared) =>
          scanMenu({
            businessId,
            listingId,
            kind,
            files: prepared,
            signal: controller.signal,
            onProgress: setDishes,
          }),
        signal: controller.signal,
      })
    } finally {
      if (running.current === controller) running.current = null
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.CREDITS_BALANCE, businessId) })
    }
  }

  return { run, cancel: () => running.current?.abort(), dishes }
}
